import React from 'react';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { StepLabel, StepIcon, styled } from '@mui/material';
import StepConnector, {
    stepConnectorClasses,
} from '@mui/material/StepConnector';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { StepperStatusEnum } from '../Constants';
import {
    ConsultantInvoicePaymentStatus,
    PAYMENT_MODE,
    InvoicingPaymentSuccessAdjustedLabel,
} from '../../invoicing/InvoicingConstants';

const DefaultStepIcon = (props) => {
    const { active, completed } = props;

    return (
        <div
            style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border:
                    completed || active
                        ? '2px solid #1360B3'
                        : '2px solid #C4C4C4',
                backgroundColor: completed || active ? '#1360B3' : '#FFFFFF',
            }}
        ></div>
    );
};

DefaultStepIcon.propTypes = {
    active: PropTypes.bool,
    completed: PropTypes.bool,
};

const CustomStepIconRoot = styled('div')(({ ownerState }) => ({
    display: 'flex',
    alignItems: 'center',
    ...(ownerState.completed && {
        color: '#1360B3',
    }),
    ...(ownerState.active && {
        color: '#1360B3',
    }),
    ...(ownerState.success && {
        color: '#1D9A60',
    }),
    ...(ownerState.failure && {
        color: '#D41B1B',
    }),
}));

// CustomStepIcon Component: Renders a custom step icon based on the currently active step.
export const CustomStepIcon = (props) => {
    const { active, completed, status } = props;

    return (
        <CustomStepIconRoot
            ownerState={{
                active,
                completed,
                success: status === StepperStatusEnum.SUCCESS,
                failure: status === StepperStatusEnum.FAILURE,
            }}
        >
            {status === StepperStatusEnum.SUCCESS ? (
                <FaCheckCircle size={14} />
            ) : status === StepperStatusEnum.FAILURE ? (
                <FaExclamationCircle size={14} />
            ) : (
                <DefaultStepIcon {...props} />
            )}
        </CustomStepIconRoot>
    );
};

CustomStepIcon.propTypes = {
    active: PropTypes.bool,
    completed: PropTypes.bool,
    status: PropTypes.string,
};

/**
 * CustomConnector Component: Render a styled connector for the stepper.
 *
 * styled(StepConnector) is a function from Material-UI's styling solution that allows you to create a custom styled component. It takes the StepConnector component and returns a new component with the applied styles.
 *
 *  Selectors:
 * .${stepConnectorClasses.className}: This syntax is used to target specific classes provided by the StepConnector component. stepConnectorClasses is an object containing all the class names used by the StepConnector.
 */
export const CustomConnector = styled(StepConnector)(() => ({
    // Styles for the connector when using the alternative label layout
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 6,
        left: 'calc(-50% + 12px)',
        right: 'calc(50% + 12px)',
    },
    // Styles for the connector when the step is active
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#1360B3',
            borderTopStyle: 'solid',
        },
    },
    // Styles for the connector when the step is completed
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#1360B3',
            borderTopStyle: 'solid',
        },
    },
    // Default styles for the line element within the connector
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#C4C4C4',
        borderTopWidth: 3,
        borderTopStyle: 'dashed',
    },
}));

const getAdjustedStatusLabel = (statusLabel, paymentMethod) => {
    // For Payment Successful stage with check payment method, display "Check Dispatched"
    if (
        statusLabel === ConsultantInvoicePaymentStatus.PAYMENT_SUCCESSFUL &&
        paymentMethod === PAYMENT_MODE.MAIL_CHEQUE
    ) {
        return InvoicingPaymentSuccessAdjustedLabel[paymentMethod];
    }

    return statusLabel;
};

// CustomStepLabel Component: Renders a step label with optional top and bottom labels and a custom icon.
export const CustomStepLabel = ({
    statusLabel,
    StepIconComponent,
    status,
    dateLabel,
    isEstimatedDate,
    paymentMethod,
}) => {
    const StepIconStyled = StepIconComponent || StepIcon;

    const _shouldRenderDateLabel = () => {
        if (isEstimatedDate) {
            // Only render estimated date for Payment Successful (SUCCESS)
            return status === StepperStatusEnum.INCOMPLETE && dateLabel !== '-';
        }

        // For actual statusUpdatedOn dates, render for ACTIVE, SUCCESS, FAILURE if the date is available
        return (
            _.includes(
                [
                    StepperStatusEnum.ACTIVE,
                    StepperStatusEnum.SUCCESS,
                    StepperStatusEnum.FAILURE,
                ],
                status
            ) && dateLabel !== '-'
        );
    };

    const _getStatusLabelColor = () =>
        status === StepperStatusEnum.SUCCESS
            ? '#1D9A60'
            : status === StepperStatusEnum.FAILURE
            ? '#D41B1B'
            : 'inherit';

    const _getDateLabelColor = () =>
        status === StepperStatusEnum.SUCCESS ? '#1D9A60' : '#00000099';

    const _getDateBackgroundColor = () =>
        isEstimatedDate ? '#E5E5E5' : '#FFFFFF';

    const _getDatePrefix = () => (isEstimatedDate ? 'Est: ' : 'on ');

    return (
        <div style={{ textAlign: 'center' }}>
            <StepLabel
                StepIconComponent={(props) => (
                    <StepIconStyled {...props} status={status} />
                )}
            >
                <div
                    style={{
                        color: _getStatusLabelColor(),
                    }}
                    data-testid={`stepper-status-label-${_.kebabCase(
                        statusLabel
                    )}`}
                >
                    {getAdjustedStatusLabel(statusLabel, paymentMethod)}
                </div>
            </StepLabel>
            <div
                style={{
                    minHeight: 16,
                    padding: '0 4px',
                    width: 110,
                    borderRadius: 2,
                    marginTop: 2,
                    fontSize: 10,
                    justifySelf: 'center',
                    color: _getDateLabelColor(),
                    backgroundColor: _getDateBackgroundColor(),
                    whiteSpace: 'nowrap',
                }}
                data-testid={`stepper-date-${_.kebabCase(statusLabel)}`}
            >
                {_shouldRenderDateLabel() && (
                    <>
                        {_getDatePrefix()}
                        {dateLabel}
                    </>
                )}
            </div>
        </div>
    );
};

CustomStepLabel.propTypes = {
    statusLabel: PropTypes.string,
    StepIconComponent: PropTypes.any,
    status: PropTypes.string,
    dateLabel: PropTypes.string,
    isEstimatedDate: PropTypes.bool,
    paymentMethod: PropTypes.string,
};
